import type { ConnectionItemProps } from "@wayflyer/flyui";
import { IconPage24Line } from "@wayflyer/flyui-icons/24/line";
import type { FundingApplicationSchemas } from "api-types";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import { CONNECT_APP_ROUTE_PATHS } from "../../../components/constants";
import type { FormattedFundingApplicationScreen } from "../../../queries/funding-application/queries";
import { getRequiredDocsFromConfiguration } from "../../../hooks/navigation-next/use-funding-application-navigation-next";
import { isDocumentOptionalNext } from "../../../utils/funding-journey";
import type { FundingApplicationDocumentType } from "../../../types";

import { DOC_TYPE_TO_NAME } from "./constants";

export const useAdditionalDocumentsAsConnectionItems = (
  fundingApplication: FormattedFundingApplicationScreen,
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen,
  onDocumentRemove: (type: FundingApplicationDocumentType, isOptional: boolean) => void,
  documents?: FundingApplicationSchemas.ListDocumentsResponse[]
): ConnectionItemProps[] => {
  const { navigate } = useSpaNavigate("FundingApp");
  const intl = useIntl();

  const allRequiredDocuments = getRequiredDocsFromConfiguration(fundingApplication, fundingApplicationStages);
  // Filter out broker-specific document types that aren't applicable to funding application
  const requiredDocuments = allRequiredDocuments.filter(
    (docType): docType is FundingApplicationDocumentType =>
      docType !== "broker-application-form" && docType !== "bank-statement" && docType !== "other"
  );

  // Document type marked by customer as "we don't have this document"
  const isDocumentMarkedAsSkipped: Record<FundingApplicationDocumentType, boolean> = {
    susa: false,
    "pool-bancario": !!fundingApplication.isPoolBancarioSkipped,
    "cash-flow-forecast": !!fundingApplication.isCashflowForecastSkipped,
    "purchase-order": !!fundingApplication.isPurchaseOrdersSkipped,
    "debt-agreement": !!fundingApplication.isCompanyDebtAgreementSkipped,
    "management-accounts": !!fundingApplication.accountingOptedOut,
    "profit-and-loss": !!fundingApplication.accountingOptedOut,
    "balance-sheet": !!fundingApplication.accountingOptedOut,
  };

  const items: ConnectionItemProps[] = [];

  requiredDocuments.forEach((documentType) => {
    const matchingDocuments = documents ? documents.find((upload) => upload.documentType === documentType) : undefined;
    const label = intl.formatMessage(DOC_TYPE_TO_NAME[documentType]);

    const isAccountingDoc = ["profit-and-loss", "balance-sheet", "management-accounts"].includes(documentType);

    // Do not include accounting docs in doc output (this is handled by validated-documents.tsx
    if (isAccountingDoc) {
      return;
    }
    if (isDocumentMarkedAsSkipped[documentType]) {
      items.push({
        testId: `document-skipped-${documentType}`,
        label,
        logo: <IconPage24Line />,
        badgeStatus: "positive",
        badgeText: intl.formatMessage({ id: "funding-application:completed", defaultMessage: "Completed" }),
      });
    } else if (matchingDocuments) {
      items.push({
        testId: `document-uploaded-${documentType}`,
        label,
        logo: <IconPage24Line />,
        badgeStatus: "positive",
        badgeText: intl.formatMessage(COMMON_MESSAGES.uploaded),
        subtitle: (
          <ul>
            {matchingDocuments.data.map((doc) => (
              <li key={doc.id}>{doc.name}</li>
            ))}
          </ul>
        ),
      });
    } else {
      items.push({
        testId: `document-todo-${documentType}`,
        label,
        logo: <IconPage24Line />,
        actions: [
          {
            critical: true,
            content: intl.formatMessage({
              id: "funding-application: remove platform label",
              defaultMessage: "Remove",
            }),
            onAction: () =>
              onDocumentRemove(documentType, isDocumentOptionalNext(documentType, fundingApplicationStages)),
          },
          {
            content: intl.formatMessage(COMMON_MESSAGES.upload),
            onAction: () => navigate(`${CONNECT_APP_ROUTE_PATHS.UPLOADS}?document=${documentType}`),
          },
        ],
      });
    }
  });
  return items;
};
